import {FormEvent, useEffect, useState} from "react";
import {Alert, Badge, Button, Card, Col, Form, Row, Table} from "react-bootstrap";
import {Link, useParams} from "react-router";
import {LicensesApi} from "../../api/LicensesApi";
import {UsersApi} from "../../api/UsersApi";
import {ProductsApi} from "../../api/ProductsApi";
import {Editions, LicenseActivationViewModel, LicenseViewModel, ProductViewModel, UserViewModel} from "../../api/models";
import {useAsync} from "../../components/useAsync";
import {ErrorAlert, fieldError} from "../../components/ErrorAlert";
import {LoadingSpinner} from "../../components/LoadingSpinner";
import {PageHeader} from "../../components/PageHeader";
import {ConfirmModal} from "../../components/ConfirmModal";
import {CopyButton} from "../../components/CopyButton";
import {Field, FieldControl} from "../../components/Field";
import {SubmitButton} from "../../components/SubmitButton";
import {formatDate, formatDateTime} from "../../components/format";
import {OwnerSelect, ProductSelect} from "./LicensesPage";
import {UserNotes} from "./UserNotes";
import {isReviewLicense} from "../../components/reviewLicense";

export function LicenseDetailPage() {
    const {licenseId = ''} = useParams();
    const {data, error, loading, setData} = useAsync(
        () => Promise.all([LicensesApi.get(licenseId), UsersApi.getAll(), ProductsApi.getAll()]), [licenseId]);
    const [license, users, products] = data ?? [];
    const setLicense = (updated: LicenseViewModel) => setData(current => current && [updated, current[1], current[2]]);

    return (
        <>
            <div className="mb-2"><Link to="/admin/licenses" className="small"><i className="bi bi-arrow-left me-1"></i>All licenses</Link></div>
            <ErrorAlert error={error} messages={{ 404: 'There is no such license.' }}/>
            {loading && !data && <LoadingSpinner/>}
            {license && users && products && (
                <>
                    <PageHeader title={`${license.productName || 'Product'} license`}>
                        {license.kind === 'earlyAccess' && <Badge bg="info" text="dark" className="fs-6" title="Activates only Early Access Versions">Early Access</Badge>}
                        {isReviewLicense(license) && <Badge bg="secondary" className="fs-6" title="A reviewer's, from a press inquiry: not a sale">Review</Badge>}
                        {license.revoked && <Badge bg="danger" className="fs-6">Revoked</Badge>}
                    </PageHeader>
                    <Row className="g-4">
                        <Col lg={5}>
                            {isReviewLicense(license) && <ReviewLicenseCard license={license} onChanged={setLicense}/>}
                            <LicenseForm license={license} users={users} products={products} onSaved={setLicense}/>
                        </Col>
                        <Col lg={7}>
                            <Activations license={license} onChanged={setLicense}/>
                            {license.ownerUsername && (
                                <Card className="mt-4">
                                    <Card.Header>Notes on {license.ownerUsername}</Card.Header>
                                    <Card.Body><UserNotes username={license.ownerUsername}/></Card.Body>
                                </Card>
                            )}
                        </Col>
                    </Row>
                </>
            )}
        </>
    );
}

function LicenseForm({license, users, products, onSaved}: { license: LicenseViewModel, users: UserViewModel[], products: ProductViewModel[], onSaved(license: LicenseViewModel): void }) {
    const [productId, setProductId] = useState(license.productId);
    const [licensee, setLicensee] = useState(license.licensee);
    const [edition, setEdition] = useState(license.edition);
    const [owner, setOwner] = useState(license.ownerUsername);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [confirmRevoke, setConfirmRevoke] = useState(false);
    const [confirmReset, setConfirmReset] = useState(false);
    const [error, setError] = useState<unknown>();
    const lastReset = license.activationResets[0];

    useEffect(() => {
        setProductId(license.productId);
        setLicensee(license.licensee);
        setEdition(license.edition);
        setOwner(license.ownerUsername);
    }, [license]);

    const save = async (revoked: boolean) => {
        if (!productId) {
            setError(new Error('Choose the license\'s product.'));
            setConfirmRevoke(false);
            return;
        }
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            onSaved(await LicensesApi.update(license.id, { productId, licensee: licensee.trim(), edition, ownerUsername: owner, revoked }));
            setSaved(true);
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setConfirmRevoke(false);
        }
    };

    const resetCount = async () => {
        setBusy(true);
        setSaved(false);
        setError(undefined);
        try {
            onSaved(await LicensesApi.resetActivationCount(license.id));
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setConfirmReset(false);
        }
    };

    const submit = (e: FormEvent) => {
        e.preventDefault();
        void save(license.revoked);
    };

    return (
        <Card>
            <Card.Body>
                <div className="d-flex align-items-center gap-2 mb-3">
                    <span className="license-key fs-5">{license.key}</span>
                    <CopyButton text={license.key} label="Copy license key"/>
                </div>
                <p className="text-body-secondary small mb-2">
                    Created {formatDateTime(license.createdOn)}. {license.activationsThisYear} of {license.activationsPerYear} new
                    activations used in the last 12 months.
                    <Button size="sm" variant="link" className="p-0 ms-2 align-baseline" disabled={busy || license.activationsThisYear === 0}
                            onClick={() => setConfirmReset(true)}>Reset count…</Button>
                </p>
                {license.activationResets.length > 0 && (
                    <ul className="list-unstyled text-body-secondary small" aria-label="Activation count resets">
                        {license.activationResets.map(x => (
                            <li key={x.on}>Count reset {formatDateTime(x.on)} by {x.by} (cleared {x.cleared}).</li>
                        ))}
                    </ul>
                )}
                <ErrorAlert error={error} shownFields={['ownerUsername']}/>
                {saved && <Alert variant="success" dismissible onClose={() => setSaved(false)}>Saved.</Alert>}
                <Form onSubmit={submit}>
                    <Field className="mb-3" controlId="license-product" label="Product"
                           help={productId !== license.productId && <span className="text-warning-emphasis">Computers already activated stop renewing: the key only works for its product.</span>}>
                        <ProductSelect id="license-product" products={products} value={productId} onChange={setProductId}/>
                    </Field>
                    <Field className="mb-3" controlId="license-owner" label="Owner" error={fieldError(error, 'ownerUsername')}
                           help={license.kind === 'earlyAccess' ? 'An Early Access license only goes to an Early Access User.'
                               : isReviewLicense(license) ? "A review license is the reviewer's own: it can't go to anyone else." : undefined}>
                        {isReviewLicense(license)
                            ? <FieldControl value={license.ownerUsername} disabled readOnly/>
                            : <OwnerSelect id="license-owner" users={users} value={owner} onChange={setOwner} earlyAccess={license.kind === 'earlyAccess'}/>}
                    </Field>
                    <Field className="mb-3" controlId="license-licensee" label="Licensee" help="Computers pick up a new name when they next renew.">
                        <FieldControl required value={licensee} onChange={e => setLicensee(e.target.value)}/>
                    </Field>
                    {license.kind === 'standard' && (
                        <Form.Group className="mb-4" controlId="license-edition">
                            <Form.Label>Edition</Form.Label>
                            <Form.Select value={edition} onChange={e => setEdition(e.target.value)}>
                                {[...new Set([...Editions, license.edition])].map(x => <option key={x}>{x}</option>)}
                            </Form.Select>
                        </Form.Group>
                    )}
                    <div className="d-flex flex-wrap gap-2">
                        <SubmitButton busy={busy}>Save</SubmitButton>
                        {license.revoked
                            ? <Button variant="outline-success" disabled={busy} onClick={() => save(false)}>Reinstate</Button>
                            : <Button variant="outline-danger" disabled={busy} onClick={() => setConfirmRevoke(true)}>Revoke…</Button>}
                    </div>
                </Form>
            </Card.Body>
            <ConfirmModal show={confirmRevoke} title="Revoke this license?" confirmLabel="Revoke" busy={busy}
                          onConfirm={() => save(true)} onCancel={() => setConfirmRevoke(false)}>
                <p>The key stops activating new computers straight away.</p>
                <p className="mb-0 text-body-secondary">
                    Computers already running it stop at their next renewal (within 10 days while online), or when their
                    lease runs out. Revoking can be undone.
                </p>
            </ConfirmModal>
            <ConfirmModal show={confirmReset} title="Reset the activation count?" confirmLabel="Reset count" variant="warning" busy={busy}
                          onConfirm={resetCount} onCancel={() => setConfirmReset(false)}>
                <p>
                    The license can make {license.activationsPerYear} new activations again. Computers using it now keep their seats:
                    free a seat separately if one is needed.
                </p>
                {lastReset
                    ? <p className="mb-0 text-warning-emphasis">It was already reset {formatDateTime(lastReset.on)} by {lastReset.by}.</p>
                    : <p className="mb-0 text-body-secondary">Meant as a one-off. Each reset is recorded on the license.</p>}
            </ConfirmModal>
        </Card>
    );
}

function Activations({license, onChanged}: { license: LicenseViewModel, onChanged(license: LicenseViewModel): void }) {
    const [freeing, setFreeing] = useState<LicenseActivationViewModel>();
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<unknown>();

    const free = async () => {
        if (!freeing) {
            return;
        }
        setBusy(true);
        setError(undefined);
        try {
            await LicensesApi.freeSeat(license.id, freeing.id);
            onChanged(await LicensesApi.get(license.id));
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
            setFreeing(undefined);
        }
    };

    return (
        <Card>
            <Card.Header>Computers ({license.seatsInUse} of {license.seats} seats)</Card.Header>
            <Card.Body>
                <ErrorAlert error={error} messages={{ 404: 'That computer was already deactivated.' }} onClose={() => setError(undefined)}/>
                {license.activations.length === 0 ? (
                    <p className="text-body-secondary mb-0">Not active on any computer.</p>
                ) : (
                    <Table responsive size="sm" className="mb-0">
                        <thead>
                        <tr><th>Computer</th><th>System</th><th>Version</th><th>Activated</th><th>Last seen</th><th></th></tr>
                        </thead>
                        <tbody>
                        {license.activations.map(x => (
                            <tr key={x.id}>
                                <td>{x.machineName || 'Unnamed computer'}</td>
                                <td className="small text-body-secondary">{x.os}</td>
                                <td>
                                    {x.productVersion}
                                    {x.devBuild && <Badge bg="secondary" className="ms-2" title="Development builds don't use a seat or one of the yearly activations">CI build</Badge>}
                                </td>
                                <td className="text-nowrap">{formatDateTime(x.activatedOn)}</td>
                                <td className="text-nowrap">{formatDateTime(x.lastSeen)}</td>
                                <td className="text-end">
                                    <Button size="sm" variant="outline-danger" onClick={() => setFreeing(x)}>Free seat</Button>
                                </td>
                            </tr>
                        ))}
                        </tbody>
                    </Table>
                )}
            </Card.Body>
            <ConfirmModal show={!!freeing} title="Free this seat?" confirmLabel="Free seat" busy={busy} onConfirm={free} onCancel={() => setFreeing(undefined)}>
                <strong>{freeing?.machineName || 'This computer'}</strong> stops running {license.productName || 'the product'} at its next renewal, or when its
                lease runs out. The yearly activation count isn't refunded.
            </ConfirmModal>
        </Card>
    );
}

// A review license (from a press inquiry): when it ends, and Extend 30 days or Keep permanently, either of which brings
// back one the daily job revoked for having ended.
function ReviewLicenseCard({license, onChanged}: { license: LicenseViewModel, onChanged(license: LicenseViewModel): void }) {
    const [busy, setBusy] = useState(false);
    const [confirmKeep, setConfirmKeep] = useState(false);
    const [error, setError] = useState<unknown>();
    const act = async (action: 'extend' | 'keep') => {
        setBusy(true);
        setError(undefined);
        setConfirmKeep(false);
        try {
            onChanged(await LicensesApi.review(license.id, action));
        } catch (err) {
            setError(err);
        } finally {
            setBusy(false);
        }
    };
    const status = license.reviewKept ? 'Kept permanently: it no longer ends.'
        : license.reviewEndedOn ? `Ended ${formatDate(license.reviewExpiresOn)}, and was revoked ${formatDateTime(license.reviewEndedOn)}.`
        : `Ends ${formatDate(license.reviewExpiresOn)}, when the daily job revokes it.`;
    return (
        <Card className="mb-4" data-testid="review-license">
            <Card.Header>Review license</Card.Header>
            <Card.Body>
                <ErrorAlert error={error} onClose={() => setError(undefined)}/>
                <p className="mb-2">{status}</p>
                <p className="small text-body-secondary">
                    A reviewer's, not a sale: 2 computers, 2 activations.
                    {license.reviewTicketId && <> Issued from <Link to={`/admin/support/${encodeURIComponent(license.reviewTicketId)}`}>their press inquiry</Link>.</>}
                </p>
                {!license.reviewKept && (
                    <div className="d-flex flex-wrap gap-2">
                        <Button variant="outline-primary" disabled={busy} onClick={() => void act('extend')}>Extend 30 days</Button>
                        <Button variant="outline-secondary" disabled={busy} onClick={() => setConfirmKeep(true)}>Keep permanently…</Button>
                    </div>
                )}
            </Card.Body>
            <ConfirmModal show={confirmKeep} title="Keep this review license permanently?" confirmLabel="Keep permanently" busy={busy}
                          onConfirm={() => void act('keep')} onCancel={() => setConfirmKeep(false)}>
                It stops ending: the reviewer keeps {license.productName || 'the product'} for good. Revoking it still works.
            </ConfirmModal>
        </Card>
    );
}
